/**
 * i18next initialization for ResumeLab.
 *
 * Supported languages: en (default), es, de, fr, pt, ar.
 * ar triggers RTL layout via a listener that updates <html dir>.
 *
 * We use in-code JSON resources (no HTTP backend) so the translations
 * ship bundled and fail gracefully if a key is missing.
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import es from "./locales/es.json";
import de from "./locales/de.json";
import fr from "./locales/fr.json";
import pt from "./locales/pt.json";
import ar from "./locales/ar.json";

export const SUPPORTED_LANGS = [
  { code: "en", label: "EN", native: "English",   flag: "🇬🇧" },
  { code: "es", label: "ES", native: "Español",   flag: "🇪🇸" },
  { code: "de", label: "DE", native: "Deutsch",   flag: "🇩🇪" },
  { code: "fr", label: "FR", native: "Français",  flag: "🇫🇷" },
  { code: "pt", label: "PT", native: "Português", flag: "🇵🇹" },
  { code: "ar", label: "AR", native: "العربية",   flag: "🇸🇦" },
];

export const RTL_LANGS = new Set(["ar", "he", "fa", "ur"]);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      de: { translation: de },
      fr: { translation: fr },
      pt: { translation: pt },
      ar: { translation: ar },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGS.map((l) => l.code),
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "resumelab_ui_lang",
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
  });

// Apply RTL / LTR based on resolved language + updates on switch
function applyDir(lng) {
  const base = (lng || "en").split("-")[0].toLowerCase();
  const dir = RTL_LANGS.has(base) ? "rtl" : "ltr";
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", base);
  }
}

applyDir(i18n.language);
i18n.on("languageChanged", applyDir);

export default i18n;
